import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, ChevronLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

import { useAuth, formatApiError } from "@/shared/context/AuthContext";
import AuthGoogleButton from "@/shared/components/AuthGoogleButton";
import LinkCityScene from "@/apps/rider/components/LinkCityScene";
import { hideRecaptchaBadge } from "@/shared/lib/recaptcha";

/**
 * /login — cinematic sign-in over the LinkCityScene background.
 * Glass card, large headline, password visibility toggle, recaptcha
 * attribution microline (badge hidden per Google's terms).
 */
export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    hideRecaptchaBadge();
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const u = await login(email, password);
      // Always land on the rider map for riders. Drivers + admins keep
      // their dedicated home; the explicit `redirect` query param still
      // wins so deep-links (e.g., /login?redirect=/trips) flow through.
      const redirect = sp.get("redirect");
      if (redirect) nav(redirect);
      else if (u.is_driver) nav("/driver");
      else if (u.is_ops_admin) nav("/admin/home");
      else nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-[#F9F7F4] text-[#0A1128] flex flex-col" data-testid="login-page">
      <header
        className="pt-[max(env(safe-area-inset-top),12px)] pb-2 px-5 sm:px-8 flex items-center justify-between"
      >
        <button
          type="button"
          onClick={() => nav("/welcome")}
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500] transition-colors"
          data-testid="login-back"
        >
          <ChevronLeft size={14} strokeWidth={2} />
          <span>Welcome</span>
        </button>
        <div aria-hidden="true" className="w-16" />
      </header>

      <div className="pt-3 pb-3 flex justify-center" data-testid="login-wordmark">
        <img src="/assets/link-full-logo.svg?v=13" alt="Link" className="h-9 sm:h-10 w-auto" data-testid="login-logo" />
      </div>

      <main className="flex-1 w-full max-w-md mx-auto px-6 sm:px-8 pb-10">
        <div className="text-center mb-6">
          <div className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]">
            Sign in
          </div>
          <h1 className="font-serif text-[32px] sm:text-[36px] leading-tight tracking-tight mt-2 text-[#0A1128]" data-testid="login-headline">
            Welcome back.
          </h1>
          <p className="text-[14px] text-[#515E7B] mt-2 max-w-sm mx-auto" data-testid="login-sub">
            Manage your transportation plans, payments, and upcoming transportation.
          </p>
        </div>

        <div
          className="bg-white border border-black/[0.06] rounded-3xl p-6 sm:p-7 shadow-sm"
          data-testid="login-card"
        >
          {/* C2 · Continue with Google (Emergent-managed OAuth). Sits
              above the email/password fallback so wallet-native riders
              get a one-tap path. */}
          <AuthGoogleButton testId="login-google-button" />
          <div
            className="flex items-center gap-3 my-5 text-[10px] tracking-[0.28em] uppercase text-[#515E7B]/50"
            aria-hidden="true"
          >
            <span className="flex-1 h-px bg-black/[0.08]" />
            <span>Or</span>
            <span className="flex-1 h-px bg-black/[0.08]" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Email">
              <input
                className="auth-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="you@email.com"
                data-testid="login-email"
              />
            </Field>
            <Field
              label="Password"
              hint={
                <Link
                  to="/forgot-password"
                  className="text-[10.5px] tracking-[0.2em] uppercase font-semibold text-[#ed6500] hover:text-[#ff8a36]"
                  data-testid="forgot-password-link"
                >
                  Forgot?
                </Link>
              }
            >
              <div className="relative">
                <input
                  className="auth-input pr-10"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="Your password"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#515E7B] hover:text-[#ed6500]"
                  data-testid="login-toggle-password"
                >
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>

            <button
              type="submit"
              disabled={busy}
              className="w-full h-14 bg-[#ed6500] hover:bg-[#ff7b1f] active:bg-[#d75600] disabled:opacity-60 text-white rounded-2xl text-[13px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="login-submit"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              {busy ? "Signing in…" : "Sign in"}
            </button>

            <p className="text-[10px] text-[#515E7B]/65 text-center leading-relaxed mt-3">
              Protected by reCAPTCHA ·{" "}
              <a className="hover:text-[#ed6500]" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
              {" · "}
              <a className="hover:text-[#ed6500]" href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms</a>
            </p>
          </form>

          <div className="mt-6 pt-5 border-t border-black/[0.06] text-center">
            <span className="text-[12px] text-[#515E7B]">New to Link?</span>{" "}
            <Link
              to="/onboarding"
              className="text-[12px] font-semibold text-[#ed6500] hover:text-[#ff8a36]"
              data-testid="login-create-account-link"
            >
              Create your account
            </Link>
          </div>
        </div>
      </main>

      {/* auth-input — light surface */}
      <style>{`
        .auth-input {
          width: 100%;
          background: #FFFFFF;
          color: #0A1128;
          padding: 14px 14px;
          border: 1px solid rgba(10,17,40,0.10);
          border-radius: 12px;
          font-size: 15px;
          outline: none;
          transition: border-color 180ms, box-shadow 180ms;
        }
        .auth-input::placeholder { color: rgba(81,94,123,0.55); }
        .auth-input:focus {
          border-color: #ed6500;
          box-shadow: 0 0 0 4px rgba(237,101,0,0.08);
        }
      `}</style>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B]">
          {label}
        </label>
        {hint}
      </div>
      {children}
    </div>
  );
}
